/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { useNavigate, useParams } from 'react-router-dom'
import { doc, collection, getDoc, updateDoc, DocumentSnapshot, onSnapshot, query, where, orderBy } from 'firebase/firestore'
import { db } from '../../firebase'
import { Box, Typography, Table, TableBody, TableHead, TableRow, TableCell, TableContainer, Paper, Backdrop, CircularProgress, Stack, Fab, Link, useTheme, Tabs, Tab } from "@mui/material"
import FormElement from "../lib/FormElement"
import isEmpty from 'lodash.isempty'
import { validate } from "../lib/FormValidation"
import parse from 'html-react-parser'
import { toast } from 'react-toastify'
import dayjs from "dayjs"
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import { tokens } from "../../theme"
import { IndividualStatusIcon } from "./lib/IndividualStatusIcon"

const HunterDetails = () => {
  const urlParams = useParams<{ hunterId: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const [forms, setForms] = useState<any>()
  const [formValues, setFormValues] = useState<any>()
  const [error, setError] = useState<string>('')
  const [prosessing, setProcessing] = useState<boolean>(false)
  const [individuals, setIndividuals] = useState<Array<object>>([])
  const [tabValue, setTabValue] = useState<number>(0)

  useEffect(() => {
    getDoc(doc(collection(db, 'forms'), 'hunter')).then((_: DocumentSnapshot) => {
      _.exists() && setForms(_.data().conf)
    })
  }, [urlParams])

  useEffect(() => {
    if (!forms) return
    const unsubscribe = onSnapshot(doc(collection(db, 'hunters'), urlParams.hunterId), (_: DocumentSnapshot) => {
      const defaultValues = forms.reduce((a: any, b: any) => {
        a[b.name] = ""
        return a
      }, {})
      if(!_.exists()) return navigate('/noData')
      const data = _.data()
      const mergedValues = { ...defaultValues, ...data }
      setFormValues(mergedValues)
    })
    return unsubscribe
  }, [forms])

  // 狩猟者の個体一覧を取得
  useEffect(() => {
    if (!formValues || !formValues.name) return

    const individualsRef = collection(db, 'individuals')
    const q = query(
      individualsRef,
      where("name", "==", formValues.name),
      orderBy("receptionDatetime", "desc")
    )

    const unsubscribe = onSnapshot(q, ({ docs }) => {
      setIndividuals(docs.map((_, i) => {
        return ({ No: i + 1, ..._.data(), id: _.id })
      }))
    })

    return unsubscribe
  }, [formValues])

  const onChange = ({name, value}) => {
    setFormValues({...formValues, [name]: value})
  }
  const update = async () => {
    const errors = forms.map((form: any) => {
      return validate(formValues[form.name], form.label, form.validation)
    })
    const errorMessages = errors.filter((e: any) => !isEmpty(e)).join("<br/>")
    if (!isEmpty(errorMessages)) {
      setError(errorMessages)
      return
    }
    setProcessing(true)
    updateDoc(doc(collection(db, 'hunters'), urlParams.hunterId),
      { ...formValues, updatedAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm') })
      .then(() => {
        toast.success("狩猟者情報を更新しました")
        setError("")
      }).catch(e => {
        toast.error(e)
      }).finally(() => {
        setProcessing(false)
      })
  }

  const getValue = (name: string, type?: string) => {
    switch (type) {
      case "upload":
        return formValues[name].data
      default:
        return formValues[name]
    }
  }

  const changeState = (isActive: boolean) => {
    setProcessing(true)
    updateDoc(doc(collection(db, 'hunters'), urlParams.hunterId),
      { ...formValues, updatedAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm'), status: isActive ? "active" : "suspended" })
      .then(() => {
        toast.success("狩猟者情報を更新しました")
        setError("")
      }).catch(e => {
        toast.error(e)
      }).finally(() => {
        setProcessing(false)
      })
  }

  // タブ変更ハンドラー
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  // 個体一覧のカラム定義
  const individualColumns = [
    {
      field: "No",
      headerName: "No",
      width: 60,
      sortComparator: gridNumberComparator,
    },
    {
      field: "receptionDatetime",
      headerName: "受付日時",
      width: 150,
    },
    {
      field: "arrivalDatetime",
      headerName: "搬入日時",
      width: 150,
    },
    {
      field: "sex",
      headerName: "性別",
      width: 80,
    },
    {
      field: "age",
      headerName: "推定年齢",
      width: 100,
    },
    {
      field: "weight",
      headerName: "体重",
      width: 100,
      valueFormatter: (params: any) => {
        return params.value ? `${params.value} kg` : '';
      },
    },
    {
      field: "individualNo",
      headerName: "個体番号",
      width: 120,
    },
    {
      field: "detail",
      headerName: "詳細",
      width: 100,
      renderCell: (params: any) => (
        <Link color="secondary" style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/individuals/${params.row.id}`)}>
          <IndividualStatusIcon status={params.row.status} size="small" />
        </Link>
      ),
    }
  ]

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
    >
      <Box
        m={2}
        width="90%"
        minHeight="100vh"
        display="flex-col"
        mt={5}
      >
        {/* タブナビゲーション */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="狩猟者詳細タブ">
            <Tab label="狩猟者情報" />
            <Tab label={`捕獲個体一覧 (${individuals.length}件)`} />
          </Tabs>
        </Paper>

        {/* タブコンテンツ */}
        {tabValue === 0 && (
          <Box>
            <TableContainer component={Paper}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">項目</TableCell>
                    <TableCell width="20%" align="left">入力内容</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ background: formValues && ["registed", "suspended"].includes(formValues["status"]) ? "silver" : "white" }}>
                  {
                    forms && formValues && forms.map((form: any) => (
                      <TableRow
                        key={form.name}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          {form.label}{form.type === 'checkbox' ? ` (${form.title})` : ""}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          <FormElement
                              type={form.type}
                              name={form.name}
                              value={getValue(form.name)}
                              onChange={onChange}
                              options={form.options}
                              text={form.text}
                              // disabled={formDisabled}
                            />
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </TableBody>
              </Table>
            </TableContainer>

            <Box mt={2} display="flex" justifyContent="center">
              <Typography variant="body2" color="red">{parse(error)}</Typography>
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                捕獲個体一覧 ({individuals.length}件)
              </Typography>
              <Box
                height="600px"
                sx={{
                  "& .MuiDataGrid-root": {
                    border: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.blueAccent[700],
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: colors.primary[400],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "none",
                    backgroundColor: colors.blueAccent[700],
                  },
                }}
              >
                <DataGrid
                  rows={individuals}
                  columns={individualColumns}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 15 },
                    },
                  }}
                  pageSizeOptions={[10, 15, 25, 50]}
                />
              </Box>
            </Paper>
          </Box>
        )}

        {/* 固定ボタン（狩猟者情報タブでのみ表示） */}
        {tabValue === 0 && (
          <Box mt={2} display="flex" justifyContent="center">
            <Stack direction="row" spacing={3}>
              {
                formValues && ["registed", "suspended"].includes(formValues["status"]) ?
                  <Fab variant="extended" color="warning" onClick={() => changeState(true)} style={{ right: 20, bottom: 80, position: "fixed" }}>&emsp;有&emsp;効&emsp;化&emsp;</Fab> :
                  <Fab variant="extended" color="success" onClick={() => changeState(false)} style={{ right: 20, bottom: 80, position: "fixed" }}>&emsp;無&emsp;効&emsp;化&emsp;</Fab>
              }
              <Fab variant="extended" color="info" onClick={update} style={{ right: 20, bottom: 20, position: "fixed" }}>&emsp;更&emsp;&emsp;新&emsp;</Fab>
            </Stack>
          </Box>
        )}
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={prosessing}
        onClick={() => setProcessing(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  )
}
export default HunterDetails
